import { action, makeAutoObservable } from "mobx";
import _ from "lodash";
import { safe_get } from "../../report-visuals/report-utils";
import { FilterTuple, TransformerConfig, Summary, GeopolDocument, ShadowIndexStats } from "./types";
import { toast } from "react-toastify";

export class GeopolTransformerStore {
  allDocuments: GeopolDocument[] = [];
  filteredDocuments: GeopolDocument[] = [];
  transformerConfig: TransformerConfig = {
    column_transformers: [],
    row_transformers: {},
  };
  filters: FilterTuple[] = [];
  loading: boolean = false;
  summary: Summary = {
    total: 0,
    mapped: 0,
    pending: 0,
  };
  selectedDocumentId: string | null = null;
  selectedDocument: GeopolDocument | null = null;
  selectedDocumentDetails: any = null;
  loadingDetails: boolean = false;
  showColumnTransformerModal: boolean = false;
  
  // Shadow Index Stats
  showShadowStatsDrawer: boolean = false;
  shadowIndexStats: ShadowIndexStats | null = null;
  loadingShadowStats: boolean = false;
  showReindexConfirmModal: boolean = false;
  reindexing: boolean = false;

  rootStore: any;

  constructor(rootStore: any) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  @action
  fetchDocuments = async () => {
    this.loading = true;
    try {
      const payload = {
        ref_str: "p993_s2319",
      };
      const response = await this.rootStore.api.post(
        "/geopol/get_all_documents",
        payload
      );
      this.allDocuments = response.data["documents"] || [];
      this.applyFilters();
      this.calculateSummary();
    } catch (error) {
      console.error("Error fetching documents:", error);
      this.allDocuments = [];
      this.filteredDocuments = [];
    } finally {
      this.loading = false;
    }
  };

  @action
  fetchTransformerConfig = async () => {
    try {
      const response = await this.rootStore.api.post(
        "/geopol/get_transformer_payload",
        { ref_str: "p993_s2319" }
      );
      this.transformerConfig = response.data || {
        column_transformers: [],
        row_transformers: {},
      };
      this.calculateSummary();
    } catch (error) {
      console.error("Error fetching transformer config:", error);
    }
  };

  @action
  calculateSummary = () => {
    const total = this.allDocuments.length;
    const mapped = Object.keys(this.transformerConfig.row_transformers || {})
      .length;
    const pending = Math.max(0, total - mapped);
    this.summary = { total, mapped, pending };
  };

  @action
  applyFilter = (key: string, val: any, display: string) => {
    const filters = _.clone(this.filters);
    // Remove existing filter with same key
    _.remove(filters, (f) => f.key === key);
    // Add new filter
    filters.push({ key, val, display });
    this.filters = filters;
    this.applyFilters();
  };

  @action
  removeFilter = (key: string) => {
    const filters = _.clone(this.filters);
    _.remove(filters, (f) => f.key === key);
    this.filters = filters;
    this.applyFilters();
  };

  @action
  resetFilters = () => {
    this.filters = [];
    this.applyFilters();
  };

  @action
  applyFilters = () => {
    if (this.filters.length === 0) {
      this.filteredDocuments = this.allDocuments;
      return;
    }

    this.filteredDocuments = this.allDocuments.filter((doc) => {
      return this.filters.every((filter) => {
        return this.matchesFilter(doc, filter);
      });
    });
  };

  private matchesFilter = (doc: GeopolDocument, filter: FilterTuple): boolean => {
    const { key, val } = filter;

    // Handle nested fields (e.g., demographic_profile_of_respondents.gender_gender_select)
    if (key.includes(".")) {
      const value = safe_get(doc, key, null);
      // Handle null, undefined, and empty string cases
      if (value === null || value === undefined || value === "") {
        return val === null || val === undefined || val === "" || String(val) === "N/A";
      }
      return String(value).trim() === String(val).trim();
    }

    // Handle direct fields
    if (key === "surveyor") {
      // Special handling for surveyor: check name (since breakdown uses name)
      const surveyorValue = doc.surveyor_name || doc.surveyor_id || "";
      return String(surveyorValue).trim() === String(val).trim();
    }

    if (key === "dt_month_date") {
      const dateValue = doc.dt_month_date || "";
      return String(dateValue).trim() === String(val).trim();
    }

    // Direct field access
    const fieldValue = doc[key] || "";
    return String(fieldValue).trim() === String(val).trim();
  };

  @action
  setSelectedDocument = (documentId: string | null) => {
    this.selectedDocumentId = documentId;
    if (documentId) {
      this.selectedDocument =
        this.allDocuments.find((doc) => doc.id === documentId) || null;
      this.fetchDocumentDetails(documentId);
    } else {
      this.selectedDocument = null;
      this.selectedDocumentDetails = null;
    }
  };

  @action
  fetchDocumentDetails = async (documentId: string) => {
    this.loadingDetails = true;
    try {
      const response = await this.rootStore.api.post(
        "survey/getbyidwithoutaudio",
        { clientid: documentId }
      );
      this.selectedDocumentDetails = response.data;
    } catch (error) {
      console.error("Error fetching document details:", error);
      this.selectedDocumentDetails = null;
    } finally {
      this.loadingDetails = false;
    }
  };

  @action
  saveProfile = async (clientid: string, demographicOverride: any, generatedProfile: any) => {
    if (!clientid) return;
    
    try {
      // Save to backend
      const response = await this.rootStore.api.post(
        "/geopol/save_profile",
        {
          ref_str: "p993_s2319",
          clientid: clientid,
          demographic_override: demographicOverride,
          generated_profile: generatedProfile,
        }
      );

      if (response.data?.status === 'success') {
        // Refresh transformer config to get updated row_transformers
        await this.fetchTransformerConfig();
        
        // Update the document in the local store
        const docIndex = this.allDocuments.findIndex(
          (doc) => doc.id === clientid
        );
        if (docIndex !== -1) {
          if (!this.allDocuments[docIndex].demographic_profile_of_respondents) {
            this.allDocuments[docIndex].demographic_profile_of_respondents = {};
          }
          Object.assign(
            this.allDocuments[docIndex].demographic_profile_of_respondents,
            demographicOverride
          );
          this.applyFilters();
          
          // Update selected document if it's the same
          if (this.selectedDocument?.id === clientid) {
            this.selectedDocument = { ...this.allDocuments[docIndex] };
          }
        }
        
        // Show success toast
        toast.success("Profile saved successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        // Navigate back to list view
        this.setSelectedDocument(null);
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  @action
  setShowColumnTransformerModal = (show: boolean) => {
    this.showColumnTransformerModal = show;
  };

  @action
  saveColumnTransformers = async (jsonData: any) => {
    try {
      const response = await this.rootStore.api.post(
        "/geopol/save_column_transformers",
        {
          json_data: jsonData,
        }
      );

      if (response.data?.status === 'success') {
        // Refresh transformer config to get updated column_transformers
        await this.fetchTransformerConfig();
        
        // Close modal
        this.setShowColumnTransformerModal(false);
        
        // Show success toast
        toast.success("Column transformers saved successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error("Error saving column transformers:", error);
      toast.error("Failed to save column transformers. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  @action
  setShowShadowStatsDrawer = (show: boolean) => {
    this.showShadowStatsDrawer = show;
    if (show && !this.shadowIndexStats) {
      this.fetchShadowIndexStats();
    }
  };

  @action
  fetchShadowIndexStats = async () => {
    this.loadingShadowStats = true;
    try {
      const response = await this.rootStore.api.post(
        "/geopol/respondent_profile_stats",
        { index_name: "p993_s2319_shadow" }
      );
      this.shadowIndexStats = response.data;
    } catch (error) {
      console.error("Error fetching shadow index stats:", error);
      toast.error("Failed to load shadow index statistics", {
        position: "top-right",
        autoClose: 3000,
      });
      this.shadowIndexStats = null;
    } finally {
      this.loadingShadowStats = false;
    }
  };

  @action
  setShowReindexConfirmModal = (show: boolean) => {
    this.showReindexConfirmModal = show;
  };

  @action
  reindexShadowIndex = async () => {
    this.reindexing = true;
    this.setShowReindexConfirmModal(false);
    
    try {
      const response = await this.rootStore.api.post("/geopol/build_shadow_index", {});
      
      if (response.data?.status === 'success') {
        toast.success(`Shadow index rebuilt successfully! Processed ${response.data.processed_count} documents.`, {
          position: "top-right",
          autoClose: 5000,
        });
        // Refresh stats after reindex
        await this.fetchShadowIndexStats();
      }
    } catch (error) {
      console.error("Error reindexing shadow index:", error);
      toast.error("Failed to rebuild shadow index. Please try again later.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      this.reindexing = false;
    }
  };

  @action
  initialize = async () => {
    await Promise.all([
      this.fetchDocuments(),
      this.fetchTransformerConfig(),
    ]);
  };
}
